/* eslint-disable no-console */
import * as L from "leaflet";
import { getCategoryLabel } from "./aiMappingColors";
import { bboxCorners, LatLngPair, parseAreaCoordinates } from "./aiMappingGeo";
import {
  AiMappingAreaLike,
  AiMappingGeoDetection,
  AiMappingGeoSegmentation,
  AiMappingMapDriver,
} from "./aiMappingTypes";

const AREA_STROKE = "#ff9800";
const AREA_STROKE_SELECTED = "#1e88e5";

const toLatLng = (pair: LatLngPair): L.LatLngExpression => [pair[0], pair[1]];

const layerToPoints = (layer: L.Layer, inputType: "BBOX" | "POLYGON" | "LINE"): LatLngPair[] => {
  if (layer instanceof L.Rectangle || inputType === "BBOX") {
    const bounds = (layer as L.Rectangle).getBounds();
    return [
      [bounds.getSouthWest().lat, bounds.getSouthWest().lng],
      [bounds.getNorthEast().lat, bounds.getNorthEast().lng],
    ];
  }
  if (layer instanceof L.Polygon) {
    const rings = layer.getLatLngs();
    const ring = (Array.isArray(rings[0]) ? rings[0] : rings) as L.LatLng[];
    const points: LatLngPair[] = ring.map((latLng) => [latLng.lat, latLng.lng]);
    // Drop a closing duplicate (last point equal to first).
    const first = points[0];
    const last = points[points.length - 1];
    if (points.length > 1 && first && last && first[0] === last[0] && first[1] === last[1]) {
      points.pop();
    }
    return points;
  }
  if (layer instanceof L.Polyline) {
    return (layer.getLatLngs() as L.LatLng[]).map((latLng) => [latLng.lat, latLng.lng]);
  }
  return [];
};

const buildAreaLayer = (area: AiMappingAreaLike, selected: boolean): L.Path | null => {
  const points = parseAreaCoordinates(area.coordinates);
  if (!points.length) return null;
  const style: L.PathOptions = {
    color: selected ? AREA_STROKE_SELECTED : AREA_STROKE,
    weight: selected ? 4 : 2,
    dashArray: selected ? undefined : "6 4",
    fillColor: selected ? AREA_STROKE_SELECTED : AREA_STROKE,
    fillOpacity: 0.08,
  };
  if (area.inputType === "BBOX") {
    const corners = bboxCorners(points);
    if (corners.length !== 2) return null;
    return L.rectangle(L.latLngBounds(corners.map(toLatLng)), style);
  }
  if (points.length < 3) return null;
  return L.polygon(points.map(toLatLng), style);
};

/**
 * Leaflet implementation of the AI mapping map driver. Manages its own layer
 */
export function createAiMappingMapDriver(map: L.Map): AiMappingMapDriver {
  const areasGroup = L.layerGroup().addTo(map);
  const detectionsGroup = L.layerGroup().addTo(map);
  const segmentationsGroup = L.layerGroup().addTo(map);
  const draftGroup = L.layerGroup().addTo(map);

  let createHandler: ((event: any) => void) | null = null;
  let drawEndHandler: (() => void) | null = null;
  let clickHandler: ((latLng: LatLngPair) => void) | null = null;
  let clickHandlerAttached = false;
  let areaSelectHandler: ((areaId: string) => void) | null = null;
  let areaClearHandler: (() => void) | null = null;
  let suppressNextAreaClear = false;
  let drawActive = false;

  const handleMapClick = (event: L.LeafletMouseEvent) => {
    if (drawActive || map.pm.globalDrawModeEnabled?.()) return;
    if (suppressNextAreaClear) {
      suppressNextAreaClear = false;
      return;
    }
    if (clickHandler) {
      clickHandler([event.latlng.lat, event.latlng.lng]);
    } else {
      areaClearHandler?.();
    }
  };

  const detachSession = () => {
    drawActive = false;
    if (createHandler) {
      map.off("pm:create", createHandler);
      createHandler = null;
    }
    if (drawEndHandler) {
      map.off("pm:drawend", drawEndHandler);
      drawEndHandler = null;
    }
    (map as any)._aiMappingDrawActive = false;
  };

  const restoreContinueDrawing = () => {
    setTimeout(() => {
      map.pm.setGlobalOptions({ continueDrawing: true });
    }, 0);
  };

  const stopDrawSession = () => {
    detachSession();
    map.pm.disableDraw();
  };

  return {
    startDraw(inputType, onDrawn, onCancel, options?: { showDraft?: boolean }) {
      stopDrawSession();
      drawActive = true;
      (map as any)._aiMappingDrawActive = true;
      draftGroup.clearLayers();
      const showDraft = options?.showDraft !== false;

      (map as any)._aiMappingEndActiveDraw = () => {
        if (!drawActive) return;
        detachSession();
        draftGroup.clearLayers();
        restoreContinueDrawing();
        onCancel?.();
      };
      drawEndHandler = () => {
        setTimeout(() => {
          if (!drawActive) return;
          const otherShape = (map.pm.Draw as any)?.getActiveShape?.();
          if (otherShape) {
            detachSession();
            restoreContinueDrawing();
            onCancel?.();
            return;
          }
          stopDrawSession();
          draftGroup.clearLayers();
          restoreContinueDrawing();
          onCancel?.();
        }, 0);
      };
      map.on("pm:drawend", drawEndHandler);

      createHandler = (event: any) => {
        const layer = event.layer as L.Layer;
        if (inputType === "LINE") {
          (layer as any)._aiMappingCutLine = true;
        }
        const points = layerToPoints(layer, inputType);
        const containing = (map.pm as any)._getContainingLayer?.();
        if (
          containing &&
          containing !== map &&
          typeof (containing as any).removeLayer === "function"
        ) {
          (containing as any).removeLayer(layer);
        }
        (layer as any).remove?.();
        if (showDraft && points.length && inputType !== "LINE") {
          const draft =
            inputType === "BBOX"
              ? L.rectangle(L.latLngBounds(bboxCorners(points).map(toLatLng)), {
                  color: AREA_STROKE,
                  weight: 2,
                  fillOpacity: 0.1,
                })
              : L.polygon(points.map(toLatLng), {
                  color: AREA_STROKE,
                  weight: 2,
                  fillOpacity: 0.1,
                });
          draftGroup.addLayer(draft);
        }
        stopDrawSession();
        restoreContinueDrawing();
        onDrawn(points);
      };
      map.on("pm:create", createHandler);

      const shape = inputType === "BBOX" ? "Rectangle" : inputType === "LINE" ? "Line" : "Polygon";
      try {
        map.pm.enableDraw(shape, {
          tooltips: false,
          cursorMarker: false,
          snappable: false,
          continueDrawing: false,
          // Polygons finish on double click / clicking the first vertex.
          finishOn: inputType === "POLYGON" ? "dblclick" : undefined,
        } as any);
      } catch (error) {
        stopDrawSession();
        restoreContinueDrawing();
        console.error("[ai-mapping] enableDraw failed", error);
      }
    },

    cancelDraw() {
      const wasActive = drawActive;
      stopDrawSession();
      draftGroup.clearLayers();
      if (wasActive) restoreContinueDrawing();
    },

    clearDraft() {
      draftGroup.clearLayers();
    },

    setClickHandler(handler) {
      clickHandler = handler;
      if (!clickHandlerAttached) {
        map.on("click", handleMapClick);
        clickHandlerAttached = true;
      }
    },

    syncAreas(areas, selectedAreaId, onSelectArea, onClearArea) {
      areaSelectHandler = onSelectArea ?? null;
      areaClearHandler = onClearArea ?? null;
      if (!clickHandlerAttached) {
        map.on("click", handleMapClick);
        clickHandlerAttached = true;
      }
      areasGroup.clearLayers();
      for (const area of areas) {
        const layer = buildAreaLayer(area, area.id === selectedAreaId);
        if (layer) {
          layer.on("click", (event) => {
            if (drawActive || map.pm.globalDrawModeEnabled?.()) return;
            suppressNextAreaClear = true;
            setTimeout(() => {
              suppressNextAreaClear = false;
            }, 0);
            event.originalEvent?.stopPropagation();
            areaSelectHandler?.(area.id);
          });
          areasGroup.addLayer(layer);
        }
      }
    },

    syncDetections(detections: AiMappingGeoDetection[], selectedDetectionId, onSelect) {
      detectionsGroup.clearLayers();
      for (const { detection, color, position, areaId } of detections) {
        if (!position) continue;
        const selected = detection.id === selectedDetectionId;
        const marker = L.circleMarker(toLatLng(position), {
          radius: selected ? 8 : 6,
          color: selected ? "#111827" : "#ffffff",
          weight: selected ? 2 : 1,
          fillColor: color,
          fillOpacity: 0.95,
        });
        const confidence =
          typeof detection.confidence === "number"
            ? ` · ${Math.round(detection.confidence * 100)}%`
            : "";
        marker.bindTooltip(`${getCategoryLabel(detection.category)}${confidence}`, {
          direction: "top",
          offset: [0, -6],
        });
        if (onSelect) {
          marker.on("click", (event) => {
            if (drawActive || map.pm.globalDrawModeEnabled?.()) return;
            suppressNextAreaClear = true;
            setTimeout(() => {
              suppressNextAreaClear = false;
            }, 0);
            event.originalEvent?.stopPropagation();
            onSelect(detection.id, areaId);
          });
        }
        detectionsGroup.addLayer(marker);
      }
    },

    syncSegmentations(
      segmentations: AiMappingGeoSegmentation[],
      visible,
      selectedSegmentationId,
      onSelectSegmentation
    ) {
      segmentationsGroup.clearLayers();
      if (!visible) return;
      for (const { segmentation, color, ring, areaId } of segmentations) {
        if (!ring || ring.length < 3) continue;
        const selected = segmentation.id === selectedSegmentationId;
        const polygon = L.polygon(ring.map(toLatLng), {
          color: selected ? "#111827" : color,
          weight: selected ? 3 : 1,
          fillColor: color,
          fillOpacity: selected ? 0.5 : 0.32,
        });
        if (typeof segmentation.areaSqft === "number") {
          polygon.bindTooltip(
            `${getCategoryLabel(segmentation.category)} · ${Math.round(
              segmentation.areaSqft
            )} sqft`,
            { sticky: true }
          );
        }
        if (onSelectSegmentation) {
          polygon.on("click", (event) => {
            if (drawActive || map.pm.globalDrawModeEnabled?.()) return;
            suppressNextAreaClear = true;
            setTimeout(() => {
              suppressNextAreaClear = false;
            }, 0);
            event.originalEvent?.stopPropagation();
            onSelectSegmentation(segmentation.id, areaId);
          });
        }
        segmentationsGroup.addLayer(polygon);
      }
    },

    fitToPoints(points) {
      if (!points.length) return;
      map.fitBounds(L.latLngBounds(points.map(toLatLng)), { padding: [48, 48] });
    },

    clearAll() {
      stopDrawSession();
      this.setClickHandler(null);
      areasGroup.clearLayers();
      detectionsGroup.clearLayers();
      segmentationsGroup.clearLayers();
      draftGroup.clearLayers();
    },
  };
}
